import { useParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Copy, Loader2, AlertCircle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useInteractionDetail } from '../../hooks/useInteractionDetail'
import { ChatHistory } from './ChatHistory'
import { SessionDetailsPanel } from './SessionDetails'

export function InteractionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useInteractionDetail(sessionId)

  // Invalidate interactions list when component unmounts (to refresh read/unread status)
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] })
    }
  }, [queryClient])

  const handleBack = () => {
    queryClient.invalidateQueries({ queryKey: ['interactions'] })
    navigate('/app/interactions') // Navigate to interactions list
  }

  const handleCopyEmail = () => {
    if (data?.session.visitor_email) {
      navigator.clipboard.writeText(data.session.visitor_email)
      toast({
        title: 'Email copied!',
        description: 'Email address copied to clipboard',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading interaction...</span>
        </div>
      </div>
    )
  }

  if (error || !data || !data.session) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load interaction details. Please try again.
          </AlertDescription>
        </Alert>
        <Button onClick={handleBack} className="mt-4" variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Interactions
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button onClick={handleBack} variant="outline" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Interactions
        </Button>

        <h1 className="text-xl font-bold">Interaction Details</h1>

        {data.session.visitor_email && (
          <Button onClick={handleCopyEmail} variant="outline" size="sm">
            <Copy className="h-4 w-4 mr-2" />
            Copy Email
          </Button>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Chat History (2/3 width) */}
        <div className="lg:col-span-2">
          <ChatHistory messages={data.messages} />
        </div>

        {/* Right: Session Details & Action Items (1/3 width) */}
        <div className="lg:col-span-1">
          <SessionDetailsPanel
            session={data.session}
            messageCount={data.messages.length}
            sessionId={sessionId!}
          />
        </div>
      </div>
    </div>
  )
}
